/**
 * Check to see if the input value is empty.  Will also check an object as well.
 * @param value
 * @returns {boolean}
 */
export const isEmpty  = (value:any):boolean => {
    return value === undefined ||
        value === null ||
        (typeof value === 'object' && Object.keys(value).length === 0) ||
        (typeof value === 'object' && value.length === 0) ||
        (typeof value === 'string' && (value.trim().length === 0 || value.trim() === '' || value.trim().toUpperCase() === 'NULL' ||
            value.trim().toUpperCase() === 'UNDEFINED'));
};

/**
 * Checks if an object has any fields with meaningful (non-empty) values
 * Performs deep checking for nested objects and arrays
 * @param obj - The object to check
 * @returns true if any field has a non-empty value, false otherwise
 */
export const hasNonEmptyFields = (obj: any): boolean => {
  if (obj === null || obj === undefined) {
    return false;
  }

  if (typeof obj !== 'object') {
    return false;
  }

  if (Object.keys(obj).length === 0) {
    return false;
  }

  return Object.values(obj).some(value => {
    // Null or undefined
    if (value === null || value === undefined) return false;
    
    // Strings
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 && 
             trimmed.toUpperCase() !== 'NULL' && 
             trimmed.toUpperCase() !== 'UNDEFINED';
    }
    
    // Number - any number (including 0) is meaningful
    if (typeof value === 'number') return true;
    
    // Boolean - false is meaningful (user explicitly selected it)
    if (typeof value === 'boolean') return true;
    
    // Array - check if non-empty AND has meaningful items
    if (Array.isArray(value)) {
      return value.length > 0 && value.some(item => hasNonEmptyFields(item));
    }
    
    // Nested object - recursively check
    if (typeof value === 'object') {
      return hasNonEmptyFields(value);
    }
    
    return false;
  });
};

/**
 * Filters SAT administration dates to return only valid SAT test dates
 * Removes any dates that have already passed
 * @param dates - Array of SAT date objects with value (ISO format) and label
 * @returns Filtered array containing only upcoming/valid SAT dates
 */
export const getValidSATDates = (dates: Array<{ value: string; label: string }>) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
  
  return dates.filter(date => {
    const testDate = new Date(date.value);
    return testDate >= today;
  });
};